import {
  CHARGER_STATUS,
  OCPP_STATUS,
  SERVER_SIDE_CHARGER_STATUS,
} from '../../../constant/Text.constant';
import { ChargerOptions } from '../../../services/charger.api';
import { SCOPE_TYPE } from '../../../services/utils';
import { Charger, Company } from '../../../stores/types';
import { ChargerPort } from '../../../stores/types/sessions.interface';
import { SortConfig } from './types';

export const ROW_PER_PAGE = 20;
export const ROW_MAX_SIZE = 1000;

export const buildFetchOptions = (
  status: (string | null)[] | null | undefined,
  maintenanceFlag: boolean | null | undefined,
  locationIds: (string | null)[] | null | undefined,
  searches: (string | null)[] | null | undefined,
  userScope: SCOPE_TYPE | undefined,
): ChargerOptions => {
  const serverStatus = new Set<string>();
  status?.forEach((_status) => {
    switch (_status) {
      case OCPP_STATUS.CHARGING:
      case OCPP_STATUS.FINISHING:
      case OCPP_STATUS.SUSPENDED_EV:
      case OCPP_STATUS.SUSPENDED_EVSE:
        serverStatus.add(SERVER_SIDE_CHARGER_STATUS.ONLINE.toLowerCase());
        serverStatus.add(SERVER_SIDE_CHARGER_STATUS.CHARGING.toLowerCase());
        break;
      case CHARGER_STATUS.OUT_OF_ORDER:
        // "out_of_order" is a UI-level status that can be derived even when the server reports ONLINE
        // (e.g. all ports are out-of-order) double check this
        serverStatus.add(SERVER_SIDE_CHARGER_STATUS.ONLINE.toLowerCase());
        serverStatus.add(SERVER_SIDE_CHARGER_STATUS.OUT_OF_ORDER.toLowerCase());
        break;
      case CHARGER_STATUS.AVAILABLE:
        serverStatus.add(SERVER_SIDE_CHARGER_STATUS.ONLINE.toLowerCase());
        break;
      default:
        if (_status) serverStatus.add(_status);
    }
  });
  return {
    scope: userScope,
    'filter_like[name]': searches?.join(','),
    limit: ROW_MAX_SIZE,
    offset: 0,
    'filter_eq[maintenanceFlag]': maintenanceFlag ?? undefined,
    'filter_in[status]': Array.from(serverStatus).join(','),
    'filter_in[locationId]': locationIds?.join(','),
  } as ChargerOptions;
};

const IN_USE_GROUP = new Set<string>([
  OCPP_STATUS.CHARGING,
  OCPP_STATUS.FINISHING,
  OCPP_STATUS.SUSPENDED_EV,
  OCPP_STATUS.SUSPENDED_EVSE,
]);

const getEffectiveStatusForFiltering = (charger: Charger): string | undefined => {
  const ports: ChargerPort[] = charger.ports ?? [];

  const chargingPort = ports.find((p) => p?.status.toLowerCase() === OCPP_STATUS.CHARGING);

  if (chargingPort) {
    const raw = (
      chargingPort?.rawChargerStatus?.rawChargerStatus ??
      chargingPort?.rawChargerStatus ??
      ''
    ).toLowerCase();

    if (raw === OCPP_STATUS.SUSPENDED_EV || raw === OCPP_STATUS.SUSPENDED_EVSE) {
      return raw;
    }

    return OCPP_STATUS.CHARGING;
  }

  return charger.status?.toLowerCase();
};

export const filterChargersByStatus = (
  chargerData: Charger[],
  status: (string | null)[] | null | undefined,
): Charger[] => {
  if (!chargerData || chargerData.length === 0) return [];
  if (!status || status.length === 0) return chargerData;

  const selected = status.filter(Boolean).map((s) => String(s).toLowerCase());

  const hasInUse = selected.includes(CHARGER_STATUS.IN_USE);
  const selectedWithoutInUse = selected.filter((s) => s !== CHARGER_STATUS.IN_USE);

  return chargerData.filter((charger) => {
    const effective = getEffectiveStatusForFiltering(charger);
    if (!effective) return false;

    // Status filtering is OR across selections.
    // If "in_use" is selected, treat it as an expansion to all in-use group states.
    if (hasInUse && IN_USE_GROUP.has(effective)) return true;

    return selectedWithoutInUse.includes(effective);
  });
};

const getHighestStateOfCharge = (charger: Charger): number => {
  if (!charger.ports?.length) return -1;

  const validValues = charger.ports
    .map((p) => p.stateOfCharge)
    .filter((v): v is number => v != null);

  if (!validValues.length) return -1;

  return Math.max(...validValues);
};

export const getAddress = (charger: Charger): string => {
  const { location } = charger;
  if (!location) return '';
  const { streetAddress, city, stateOrRegion, isoCountryCode, zipOrPostalCode } = location;
  return [streetAddress, city, stateOrRegion, isoCountryCode, zipOrPostalCode]
    .filter(Boolean)
    .join(', ');
};

export const sortColumns = (
  chargers: Charger[],
  sortConfig: SortConfig,
  companyMap: Record<string, Company>,
): Charger[] => {
  if (!chargers.length) return chargers;
  if (!sortConfig.column) return [...chargers];

  const getSortValue = (charger: Charger): string | number | null => {
    let value: string | number | null = null;
    switch (sortConfig.column) {
      case 'charger':
        value = charger.name?.toLowerCase() ?? null;
        break;
      case 'make':
        value = `${charger.vendor} / ${charger.model}`;
        break;
      case 'maxPower':
        value = charger.maxPowerKW ?? -1;
        break;
      case 'maxAmperage':
        value = charger.maxAmperage ?? -1;
        break;
      case 'location':
        value = charger.location?.name?.toLowerCase() ?? null;
        break;
      case 'address':
        value = getAddress(charger);
        break;
      case 'timezone':
        value = charger.location?.timeZone?.toLowerCase() ?? null;
        break;
      case 'status':
        value = charger.status?.toLowerCase() ?? null;
        break;
      case 'serialNumber':
        value = charger.serialNumber?.toLowerCase() ?? null;
        break;
      case 'ocppId':
        value = charger.ocppId?.toLowerCase() ?? null;
        break;
      case 'stateOfCharge':
        value = getHighestStateOfCharge(charger);
        break;
      case 'lastUsed':
        value = charger.lastUsed ?? null;
        break;
      case 'pricing':
        if (charger.isFree === true) value = 1;
        else if (charger.isFree === false) value = 0;
        else value = null;
        break;
      case 'pricingRule':
        value = charger.priceModelDisplayName?.toLowerCase() ?? null;
        break;
      case 'access':
        value = charger.access?.toLowerCase() ?? null;
        break;
      case 'mapVisibility':
        value = charger.hiddenOnMap === true ? 1 : 0;
        break;
      case 'powerManaged':
        value = charger.isPowerManaged === true ? 1 : 0;
        break;
      case 'autoStart':
        value = charger.autoStartEnabled === true ? 1 : 0;
        break;
      case 'parkingSpace':
        value = charger.parkingSpot?.toLowerCase() ?? null;
        break;
      case 'unit':
        value = charger.associatedBuildingUnit?.toLowerCase() ?? null;
        break;
      case 'note':
        value = charger.internalNote?.toLowerCase() ?? null;
        break;
      case 'addedDate':
        value = charger.deploymentInfo?.addedDate ?? null;
        break;
      case 'company':
        value = companyMap[charger.ownerId ?? '']?.name.toLowerCase() ?? null;
        break;
    }

    // normalize blank strings to null
    if (typeof value === 'string' && value.trim() === '') return null;

    return value;
  };

  return [...chargers].sort((charger1, charger2) => {
    const sortValue1 = getSortValue(charger1);
    const sortValue2 = getSortValue(charger2);

    if (sortValue1 == null && sortValue2 == null) return 0;
    if (sortValue1 == null) return 1;
    if (sortValue2 == null) return -1;

    let comparison = 0;

    if (typeof sortValue1 === 'number' && typeof sortValue2 === 'number') {
      comparison = sortValue1 - sortValue2;
    } else {
      comparison = String(sortValue1).localeCompare(String(sortValue2), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });
};

export const paginateChargers = (chargers: Charger[], page: number): Charger[] => {
  const startIndex = (page - 1) * ROW_PER_PAGE;
  return chargers.slice(startIndex, startIndex + ROW_PER_PAGE);
};

export const getRowBgColor = (charger: Charger): string => {
  if (charger.status === CHARGER_STATUS.OUT_OF_ORDER) return 'bg-negative0';
  if (charger.status === CHARGER_STATUS.OFFLINE) return 'bg-warning0';
  return 'bg-white';
};

export const headerMapForCSV = (t: (key: string, options?: any) => string) => {
  return {
    charger: t('charger'),
    make: t('charger_make'),
    maxPower: t('charger_max_power'),
    maxAmperage: t('charger_max_amperage'),
    serialNumber: t('serial_number'),
    ocppId: t('chargers_ocpp_id'),
    location: t('location'),
    address: t('charger_address'),
    timezone: t('location_timezone'),
    status: t('status'),
    stateOfCharge: t('chargers_grid_stateOfCharge'),
    lastUsed: t('chargers_grid_lastUsed'),
    pricing: t('pricing'),
    pricingRule: t('chargers_pricing_rule'),
    access: t('access'),
    mapVisibility: t('chargers_map_visibility'),
    powerManaged: t('charger_power_managed'),
    autoStart: t('charger_auto_start'),
    parkingSpace: t('charger_parking_space'),
    unit: t('charger_unit'),
    note: t('chargers_grid_note'),
    addedDate: t('charger_deployment_date_added'),
    company: t('company'),
  };
};

export const mapRowsForCSV = (
  chargers: Charger[],
  visibleColumnIds: string[],
  companies: Company[] | undefined,
  t: (key: string, options?: any) => string,
): string[][] => {
  return chargers.map((charger: Charger) => {
    return visibleColumnIds.map((colId) => {
      switch (colId) {
        case 'charger':
          return charger.name || '';
        case 'make':
          return `${charger.vendor || ''} / ${charger.model || ''}`;
        case 'maxPower':
          return charger.maxPowerKW?.toString() || '';
        case 'maxAmperage':
          return charger.maxAmperage?.toString() || '';
        case 'serialNumber':
          return charger.serialNumber || '';
        case 'ocppId':
          return charger.ocppId || '';
        case 'location':
          return charger.location?.name || '';
        case 'address':
          return getAddress(charger);
        case 'timezone':
          return charger.location?.timeZone || '';
        case 'status':
          return charger.status || '';
        case 'stateOfCharge': {
          const soc = getHighestStateOfCharge(charger);
          return soc >= 0 ? `${soc}%` : '';
        }
        case 'lastUsed':
          return charger.lastUsed?.toString() || '';
        case 'pricing':
          return charger.isFree ? t('chargers_price_free') : t('chargers_price_paid');
        case 'pricingRule':
          return charger.priceModelDisplayName || '';
        case 'access':
          return charger.access || '';
        case 'mapVisibility':
          return charger.hiddenOnMap
            ? t('charger_map_visibility_false')
            : t('chargers_visibility_user_access');
        case 'powerManaged':
          return charger.isPowerManaged ? t('chargers_yes') : t('chargers_no');
        case 'autoStart':
          return charger.autoStartEnabled
            ? t('charger_auto_start_status_true')
            : t('charger_auto_start_status_false');
        case 'parkingSpace':
          return charger.parkingSpot || '';
        case 'unit':
          return charger.associatedBuildingUnit || '';
        case 'note':
          return charger.internalNote || '';
        case 'addedDate':
          return charger.deploymentInfo?.addedDate || '';
        case 'company': {
          const company = companies?.find((companyInfo) => companyInfo.id === charger.ownerId);
          return company?.name || '';
        }
        default:
          return '';
      }
    });
  });
};
